import {getGameActiveCompanies, getGameMainUserCompany, getUserControlledCompanies} from "./games.js";
import {
	addCompanyCompanyLogs, addCompanyMessages,
	getCompanyChampionships,
	getCompanyEvents, getCompanyPowerups,
	getCompanyRosters
} from "../models/company.js";
import {getEventCompany, getEventMatches, getEventSegments} from "../models/event.js";
import {getSegmentEvent, getSegmentParticipants, getSegmentSegmentItem} from "../models/segment.js";
import {changeProperty, getCompanyRosterByType, processObjectiveCompleted} from "./companies.js";
import {getRosterCompany, getRosterFeuds, getRosterSuperstars} from "../models/roster.js";
import {
	captureError,
	getCurrentGame,
	getMonthsDifference,
	getRandomChoice,
	getUniqueObjects,
	objectInArray,
	shuffle,
	weightedChoice
} from "./helpers.js";
import {getRosterActiveFactions} from "./factions.js";
import {getUniqueChampionships} from "./championships.js";
import {CompanyLogs, db, loadDb} from "../models/models.js";
import {getAllSuperstarsForMatch, getMatchEvent, getMatchGimmickMatch, getMatchParticipants} from "../models/match.js";
import {getMatchEventTitle} from "./matches.js";
import {CompanyLog} from "../models/companyLog.js";
import {awardPowerup, getPowerupsToUnlock} from "./powerups.js";
import {createPowerupMessage} from "./messages.js";
import {getFactionRoster} from "../models/faction.js";
import {getChampionshipCompany} from "../models/championship.js";
import {getChampionshipDisplayName} from "../ui/ui-helpers.js";
import {getFeudRoster} from "../models/feud.js";
import {getSuperstarMatches, getSuperstarRoster} from "../models/superstar.js";
import {getFeudParticipantsNames} from "./feuds.js";

async function processAwards() {  // process_awards
	let data = {};

	try {
		const game = await getCurrentGame();
		const allCompanies = await getGameActiveCompanies(game);
		const mainCompany = await getGameMainUserCompany(game);
		const realYear = mainCompany.realYear;
		let monthlyEvents = [];
		let matches = [];
		let allFeuds = [];
		let segments = [];
		let weeklyShows = [];
		let championships = [];
		let factions = [];

		// Don't run awards if not right month and if no competitors
		if (game.awardsRun || allCompanies.length === 1 || mainCompany.storyName === 'mars') {
			return data;
		}

		// Get all events of last year that have been held (check for rating)
		let companyEvents = {};
		for (const company of allCompanies) {
			companyEvents[company.$loki] = {
				name: company.name,
				events: [],
				company: company
			};
		}
		for (const company of allCompanies) {
			for (const event of (await getCompanyLastYearsEvents(company))) {
				if (event.rating) {
					if (!event.weeklyShow) {
						monthlyEvents.push(event);
						companyEvents[company.$loki]['events'].push(event);
					}
					else {
						weeklyShows.push(event);
					}
				}
			}
			championships.push(...await getCompanyChampionships(company));

			// Get all feuds started in past year
			const mainRoster = await getCompanyRosterByType(company, 'main');
			for (const x of (await getRosterFeuds(mainRoster))) {
				if (x.started && x.rating !== null) {
					const monthsSinceStarted = getMonthsDifference(game.monthCount, game.year, x.startMonth, x.startYear);
					if (monthsSinceStarted <= 12) {
						allFeuds.push(x);
					}
				}
			}
			factions.push(...await getRosterActiveFactions(mainRoster));
		}

		// Get matches and segments of events (exclude production segments)
		for (const event of [...monthlyEvents, ...weeklyShows]) {
			let eventSegments = await getEventSegments(event);
			for (const eventSegment of eventSegments) {
				let segmentItem = await getSegmentSegmentItem(eventSegment);
				if (segmentItem && !segmentItem.productionSegment) {
					segments.push(eventSegment)
				}
			}
			matches.push(...(await getEventMatches(event)).filter(x => x.rating !== null));
		}

		// Add awards data in order
		data['awards'] = [];
		data['awards'].push(await getBestGimmickMatch(matches, game, true, realYear));
		data['awards'].push(await getBestGimmickMatch(matches, game, false, realYear));
		if (segments.length > 2) {
			data['awards'].push(await getBestSegment(segments, game, true, realYear));
			data['awards'].push(await getBestSegment(segments, game, false, realYear));
		}
		if (championships.length) {
			let uniqueChampionships = getUniqueChampionships(championships);
			// Add awards if prestige is added (JoW Paid)
			if (uniqueChampionships.filter(x => x.xpGainedThisYear !== 0).length > 2) {
				data['awards'].push(await getBestChampionship(uniqueChampionships, game, realYear));
			}
		}
		if (factions.filter(x => x.xpGainedThisYear !== 0).length > 2) {
			data['awards'].push(await getBestFaction(factions, game, realYear));
		}
		data['awards'].push(await getBestWrestler(matches, game, realYear));
		data['awards'].push(await getMostHypedEvent(monthlyEvents, game, realYear));
		data['awards'].push(await getBestEvent(monthlyEvents, game, true, realYear));
		data['awards'].push(await getBestEvent(monthlyEvents, game, false, realYear));
		if (allFeuds.length) {
			data['awards'].push(await getBestFeud(allFeuds, game, true, realYear));
			data['awards'].push(await getBestFeud(allFeuds, game, false, realYear));
		}
		let [bestCompanyData, bestCompanyContenders] = await getBestCompany(companyEvents, game, false, realYear);
		if (bestCompanyContenders.length > 1) {
			data['awards'].push(await getBestCompany(companyEvents, game, true, realYear));
		}
		data['awards'].push(bestCompanyData);
		data['awards'].push(await getBestMatch(matches, game, true, realYear));
		data['awards'].push(await getBestMatch(matches, game, false, realYear));

		// Check for objectives completed
		let options = {
			'Craziest Match of the Year': 'win gmoty',
			'Match of the Year': 'win moty',
			'Wrestler of the Year': 'win woty',
			'Event of the Year': 'win eoty',
			'Championship of the Year': 'win toty',
			'Feud of the Year': 'win foty',
			'Segment of the Year': 'win soty',
			'Company of the Year': 'win coty',
			'Most Hyped Event of the Year': 'win mheoty',
			'Faction of the Year': 'win foty'
		};
		let userControlledCompanies = await getUserControlledCompanies(game);

		for (let x of data['awards']) {
			if (!(Object.keys(x['reward']).length === 0)) {
				// Find objective property if award won
				let property = options[x['name']];
				let company = userControlledCompanies.find(y => y.name === x['companyName']);
				if (property && company) {
					let objectiveCompleted = await changeProperty({name: property, change: 1, company: company});
					if (objectiveCompleted) {
						await processObjectiveCompleted(objectiveCompleted, company);
					}
					objectiveCompleted = await changeProperty({name: 'win awards', change: 1, company: company});
					if (objectiveCompleted) {
						await processObjectiveCompleted(objectiveCompleted, company);
					}
				}
			}
		}

		// Add host
		const randomRoster = getRandomChoice(await getCompanyRosters(getRandomChoice(allCompanies)));
		const hostSuperstars = (await getRosterSuperstars(randomRoster)).filter(y => !y.monthToAppearIn);
		if (hostSuperstars.length) {
			data['host'] = getRandomChoice(hostSuperstars).name;
		}
		else {
			data['host'] = 'Al Bananaworth';
		}

		// Reset xpGainedThisYear for all championships and factions
		for (const x of [...championships, factions]) {
			x.xpGainedThisYear = 0;
		}

		game.awardsRun = true;

		db.saveDatabase();

		data['points'] = mainCompany.points;
	}
	catch (error) {
        captureError(error);
        data['error'] = true;
        await loadDb(db.filename);
    }

	return data;
}

async function getCompanyLastYearsEvents(company) {  // get_company_last_years_events
	let allEvents = await getCompanyEvents(company);
    const monthlyEvents = allEvents.filter(x => !x.weeklyShow);
    const firstEvent = monthlyEvents[monthlyEvents.length - 13];
    const lastEvent = monthlyEvents[monthlyEvents.length - 1];
	return allEvents.filter(x => x.$loki >= firstEvent.$loki && x.$loki <= lastEvent.$loki);
}

async function getBestMatch(matches, game, reverse = false, realYear) {  // get_best_match
	let data = {};

	// Get award data
	let maxRating;
	let value;
	if (reverse) {
		maxRating = Math.min(...matches.map(y => y.rating));
		data['name'] = 'Worst Match of the Year';
		data['trophy'] = 'worst';
		value = 0;
	} else {
		maxRating = Math.max(...matches.map(y => y.rating));
		data['name'] = 'Match of the Year';
		data['trophy'] = 'gold';
		value = 3;
	}

	// Get best
	let matchWeights = [];
	for (let m of matches) {
		if (m.rating === maxRating) {
			let weight = (await getMatchGimmickMatch(m)) ? 10 : 100;
			matchWeights.push([m, weight]);
		}
	}
	let bestMatch = weightedChoice(matchWeights);

	// Get nominees
	let sortedMatches = [...matches].sort((a, b) => reverse ? a.rating - b.rating : b.rating - a.rating);
	let nominees = sortedMatches.filter(x => x !== bestMatch).slice(0, 2);
	nominees.push(bestMatch);
	shuffle(nominees);

	// Get award company
	let bestMatchEvent = await getMatchEvent(bestMatch);
	let awardCompany = await getEventCompany(bestMatchEvent);

	// Add company log
	const bestMatchEventName = await getMatchEventTitle(bestMatch);
	const bestMatchParticipantsNames = (await getMatchParticipants(bestMatch)).map(y => y.name).join(' vs ');
	const bestMatchText = `${bestMatchParticipantsNames} (${bestMatchEventName})`;
	const companyLogText = `${reverse ? 'Worst Match of the Year' : 'Match of the Year'}: ${bestMatchText}`;
	await addCompanyLog(awardCompany, game, value, companyLogText, realYear);

	// Add reward
	if (reverse) {
		await addReward(data, awardCompany, game, -50)
	} else {
		await addReward(data, awardCompany, game, 200, true)
	}

	// Format data
	data['nominees'] = [];
	for (const match of nominees) {
		const matchEventName = await getMatchEventTitle(match);
		const matchParticipantsNames = (await getMatchParticipants(match)).map(y => y.name).join(' vs ');
		data['nominees'].push({
			name: `${matchParticipantsNames} (${matchEventName})`,
			rating: match.rating
		});
	}
	data['winner'] = {
		name: bestMatchText,
		rating: bestMatch.rating
	};

	return data;
}

async function getBestGimmickMatch(matches, game, reverse = false, realYear) {  // get_best_gimmick_match
	let data = {};

	// Get only gimmick matches
	let filteredMatches = [];
	for (const match of matches) {
		if (await getMatchGimmickMatch(match)) {
			filteredMatches.push(match)
		}
	}

	// If no gimmick matches
	if (filteredMatches.length < 3) {
		filteredMatches = matches;
	}

	// Get award data
	let maxRating;
	let value;
	if (reverse) {
		maxRating = Math.min(...filteredMatches.map(y => y.rating));
		data['name'] = 'Stupidest Match of the Year';
		data['trophy'] = 'worst';
		value = 0;
	} else {
		maxRating = Math.max(...filteredMatches.map(y => y.rating));
		data['name'] = 'Craziest Match of the Year';
		data['trophy'] = 'bronze';
		value = 1;
	}

	// Get best
	let bestMatches = filteredMatches.filter(x => x.rating === maxRating);
	let bestMatch = getRandomChoice(bestMatches);

	// Get nominees
	let sortedMatches = [...filteredMatches].sort((a, b) => reverse ? a.rating - b.rating : b.rating - a.rating);
	let nominees = sortedMatches.filter(x => x !== bestMatch).slice(0, 2);
	nominees.push(bestMatch);
	shuffle(nominees);

	// Get award company
	let bestMatchEvent = await getMatchEvent(bestMatch);
	let awardCompany = await getEventCompany(bestMatchEvent);

	// Add company log
	const matchEventName = await getMatchEventTitle(bestMatch);
	const bestMatchParticipantsNames = (await getMatchParticipants(bestMatch)).map(y => y.name).join(' vs ');
	const bestMatchGimmickMatch = await getMatchGimmickMatch(bestMatch);
	let bestMatchText;
	if (bestMatchGimmickMatch) {
		bestMatchText = `${bestMatchParticipantsNames} - ${bestMatchGimmickMatch.name} Match (${matchEventName})`;
	} else {
		bestMatchText = `${bestMatchParticipantsNames} (${matchEventName})`;
	}
	const companyLogText = `${reverse ? 'Stupidest Match of the Year' : 'Craziest Match of the Year'}: ${bestMatchText}`;
	await addCompanyLog(awardCompany, game, value, companyLogText, realYear);

	// Add reward
	if (reverse) {
		await addReward(data, awardCompany, game, -50)
	} else {
		await addReward(data, awardCompany, game, 50)
	}

	// Format data
	data['nominees'] = [];
	for (const match of nominees) {
		const matchEventName = await getMatchEventTitle(match);
		const matchParticipantsNames = (await getMatchParticipants(match)).map(y => y.name).join(' vs ');
		const matchGimmickMatch = await getMatchGimmickMatch(match);
		let name;
		if (matchGimmickMatch) {
			name = `${matchParticipantsNames} - ${matchGimmickMatch.name} Match (${matchEventName})`;
		} else {
			name = `${matchParticipantsNames} (${matchEventName})`;
		}
		data['nominees'].push({
			name: name,
			rating: match.rating
		});
	}
	data['winner'] = {
		name: bestMatchText,
		rating: bestMatch.rating
	};

	return data;
}

async function getBestEvent(events, game, reverse = false, realYear) {  // get_best_event
	let data = {};

	// Get award data
	let maxRating;
	let value;
	if (reverse) {
		maxRating = Math.min(...events.map(y => y.rating));
		data['name'] = 'Worst Event of the Year';
		data['trophy'] = 'worst';
		value = 0;
	} else {
		maxRating = Math.max(...events.map(y => y.rating));
		data['name'] = 'Event of the Year';
		data['trophy'] = 'gold';
		value = 3;
	}

	// Get best
	let bestEvents = events.filter(x => x.rating === maxRating);
	let bestEvent = getRandomChoice(bestEvents);

	// Get nominees
	let sortedEvents = [...events].sort((a, b) => reverse ? a.rating - b.rating : b.rating - a.rating);
	let nominees = sortedEvents.filter(x => x !== bestEvent).slice(0, 2);
	nominees.push(bestEvent);
	shuffle(nominees);

	// Get award company
	let awardCompany = await getEventCompany(bestEvent);

	// Add company log
	const companyLogText = `${reverse ? 'Worst Event of the Year' : 'Event of the Year'}: ${bestEvent.name} (${bestEvent.year})`;
	await addCompanyLog(awardCompany, game, value, companyLogText, realYear);

	// Add reward
	if (reverse) {
		await addReward(data, awardCompany, game, -50)
	} else {
		await addReward(data, awardCompany, game, 200, true)
	}

	// Format data
	data['nominees'] = nominees.map(x => {
		return {
			name: x.name,
			rating: x.rating
		};
	});
	data['winner'] = {
		name: bestEvent.name,
		rating: bestEvent.rating
	};

	return data;
}

async function getMostHypedEvent(events, game, realYear) {  // get_most_hyped_event
	let data = {};

	// Get award data
	data['name'] = 'Most Hyped Event of the Year';
	data['trophy'] = 'silver';
	let value = 2;

	// Get best
	let sortedEvents = events.sort((a, b) => b.hype - a.hype);
	let mostHypedEvent = sortedEvents[0];

	// Get nominees
	let nominees = sortedEvents.filter(x => x !== mostHypedEvent).slice(0, 2);
	nominees.push(mostHypedEvent);
	shuffle(nominees);

	// Get award company
	let awardCompany = await getEventCompany(mostHypedEvent);

	// Add company log
	const companyLogText = `Most Hyped Event of the Year: ${mostHypedEvent.name} (${mostHypedEvent.year})`;
	await addCompanyLog(awardCompany, game, value, companyLogText, realYear);

	// Add reward
	await addReward(data, awardCompany, game, 100);

	// Format data
	data['nominees'] = nominees.map(x => {
		return {
			name: x.name,
			hype: x.hype
		};
	});
	data['winner'] = {
		name: mostHypedEvent.name,
		hype: mostHypedEvent.hype
	};

	return data;
}

async function getBestWrestler(matches, game, realYear) {  // get_best_wrestler
	let data = {};

	// Get award data
	data['name'] = 'Wrestler of the Year';
	data['trophy'] = 'silver';

	// Get wrestlers who had more than a few matches
	let superstars = (await Promise.all(matches.map(x => getAllSuperstarsForMatch(x)))).flat();
	const uniqueSuperstars = getUniqueObjects(superstars);
    let mostMatches = uniqueSuperstars.map(x => {
        return {'number': superstars.filter(y => y.$loki === x.$loki).length, 'superstar': x}
    });
    mostMatches.sort((a, b) => b.number - a.number);  // Get most common objects
	let contenders = [];
	for (let superstarData of mostMatches.slice(0, Math.floor(mostMatches.length / 4))) {
		// Get average match rating
		let averageRating = (await getSuperstarMatches(superstarData['superstar']))
			.filter(match => match.rating !== null && objectInArray(match, matches))
			.map(match => match.rating)
			.reduce((a, b) => a + b, 0) / superstarData['number'];
		let contender = {'superstar': superstarData['superstar'], 'averageRating': averageRating};
		contenders.push(contender)
	}

	// Get best
	let sortedContenders = [...contenders].sort((a, b) => b.averageRating - a.averageRating);
	let bestWrestler = sortedContenders[0];

	// Get nominees
	let nominees = [];
	nominees.push(...sortedContenders.slice(0, 3));
	shuffle(nominees);

	// Get award company
	let bestWrestlerRoster = await getSuperstarRoster(bestWrestler['superstar']);
	let awardCompany = await getRosterCompany(bestWrestlerRoster);

	// Add company log
	const companyLogText = `Wrestler of the Year: ${bestWrestler['superstar']['name']} (${realYear - 1})`;
	await addCompanyLog(awardCompany, game, 2, companyLogText, realYear);

	// Add reward
	await addReward(data, awardCompany, game, 100);

	// Format data
	data['nominees'] = nominees.map(superstarData => {
		return {
			name: superstarData['superstar']['name'],
			averageRating: superstarData['averageRating']
		};
	});
	data['winner'] = {
		name: bestWrestler['superstar']['name'],
		averageRating: bestWrestler['averageRating']
	};

	return data;
}

async function getBestFeud(feuds, game, reverse = false, realYear) {  // get_best_feud
	let data = {};

	// Get award data
	let maxRating;
	let value;
	if (reverse) {
		maxRating = Math.min(...feuds.map(feud => feud.rating));
		data['name'] = 'Worst Feud of the Year';
		data['trophy'] = 'worst';
		value = 0;
	} else {
		maxRating = Math.max(...feuds.map(feud => feud.rating));
		data['name'] = 'Feud of the Year';
		data['trophy'] = 'gold';
		value = 3;
	}

	// Get best
	let bestFeuds = feuds.filter(feud => feud.rating === maxRating);
	let bestFeud = getRandomChoice(bestFeuds);

	// Get nominees
	let sortedFeuds = [...feuds].sort((a, b) => reverse ? a.rating - b.rating : b.rating - a.rating);
	let nominees = sortedFeuds.filter(x => x !== bestFeud).slice(0, 2);
	nominees.push(bestFeud);
	shuffle(nominees);

	// Get award company
	let bestFeudRoster = await getFeudRoster(bestFeud);
	let awardCompany = await getRosterCompany(bestFeudRoster);

	// Add company log
	const bestFeudParticipantsNames = (await getFeudParticipantsNames(bestFeud)).join(' vs ');
	const bestFeudText = `${bestFeudParticipantsNames} (${bestFeud.name})`;
	const companyLogText = `${reverse ? 'Worst Feud of the Year' : 'Feud of the Year'}: ${bestFeudText} (${realYear - 1})`;
	await addCompanyLog(awardCompany, game, value, companyLogText, realYear);

	// Add reward
	if (reverse) {
		await addReward(data, awardCompany, game, -50)
	} else {
		await addReward(data, awardCompany, game, 200, true)
	}

	// Format data
	data['nominees'] = [];
	for (const feud of nominees) {
		const feudParticipantsNames = (await getFeudParticipantsNames(feud)).join(' vs ');
		let name = `${feudParticipantsNames} (${feud.name})`;
		data['nominees'].push({
			name: name,
			rating: feud.rating
		});
	}
	data['winner'] = {
		name: bestFeudText,
		rating: bestFeud.rating
	};

	return data;
}

async function getBestSegment(segments, game, reverse = false, realYear) {
	let data = {};

	// Get award data
	let maxRating;
	let value;
	if (reverse) {
		maxRating = Math.min(...segments.map(segment => segment.rating));
		data['name'] = 'Worst Segment of the Year';
		data['trophy'] = 'worst';
		value = 0;
	} else {
		maxRating = Math.max(...segments.map(segment => segment.rating));
		data['name'] = 'Segment of the Year';
		data['trophy'] = 'silver';
		value = 3;
	}

	// Get best
	let bestSegments = segments.filter(segment => segment.rating === maxRating);
	let bestSegment = getRandomChoice(bestSegments);

	// Get nominees
	let sortedSegments = [...segments].sort((a, b) => reverse ? a.rating - b.rating : b.rating - a.rating);
	let nominees = sortedSegments.filter(m => m !== bestSegment).slice(0, 2);
	nominees.push(bestSegment);
	shuffle(nominees);

	// Get award company
	let bestSegmentEvent = await getSegmentEvent(bestSegment);
	let awardCompany = await getEventCompany(bestSegmentEvent);

	// Add company log
	const bestSegmentEventName = await getMatchEventTitle(bestSegment);
	const bestSegmentParticipantsNames = (await getSegmentParticipants(bestSegment)).map(y => y.name).join(' and ');
	const bestSegmentText = `${(await getSegmentSegmentItem(bestSegment))?.name}: ${bestSegmentParticipantsNames} (${bestSegmentEventName})`;
	const companyLogText = `${reverse ? 'Worst Segment of the Year' : 'Segment of the Year'}: ${bestSegmentText} (${realYear - 1})`;
	await addCompanyLog(awardCompany, game, value, companyLogText, realYear);

	// Add reward
	if (reverse) {
		await addReward(data, awardCompany, game, -50)
	} else {
		await addReward(data, awardCompany, game, 200)
	}

	// Format data
	data['nominees'] = [];
	for (const segment of nominees) {
		const segmentEventName = await getMatchEventTitle(segment);
		const segmentParticipantsNames = (await getSegmentParticipants(segment)).map(y => y.name).join(' and ');
		data['nominees'].push({
			name: `${(await getSegmentSegmentItem(segment))?.name}: ${segmentParticipantsNames} (${segmentEventName})`,
			rating: segment.rating
		});
	}
	data['winner'] = {
		name: bestSegmentText,
		rating: bestSegment.rating
	};

	return data;
}

async function getBestCompany(companyEvents, game, reverse = false, realYear) {  // get_best_company
	let data = {};
	let companiesData = [];

	// Get average rating for each company's shows
	for (let companyData of Object.values(companyEvents)) {
		let events = companyData['events'];
		if (events.length >= 4) {
			let rating = events.reduce((a, b) => a + b.rating, 0) / events.length;
			companiesData.push({company: companyData['company'], name: companyData['name'], rating: rating});
		}
	}

	// Get award data
	let awardWinner;
	let value;
	if (reverse) {
		awardWinner = companiesData.reduce((min, p) => p.rating < min.rating ? p : min, companiesData[0]);
		data['name'] = 'Worst Company of the Year';
		data['trophy'] = 'worst';
		value = 0;
	} else {
		awardWinner = companiesData.reduce((max, p) => p.rating > max.rating ? p : max, companiesData[0]);
		data['name'] = 'Company of the Year';
		data['trophy'] = 'gold';
		value = 3;
	}

	let awardCompany = awardWinner['company'];

	// Get nominees
	let sortedCompanies = [...companiesData].sort((a, b) => reverse ? a.rating - b.rating : b.rating - a.rating);
	let nominees = sortedCompanies.filter(c => c !== awardWinner).slice(0, 2);
	nominees.push(awardWinner);
	shuffle(nominees);

	// Add company log
	const companyLogText = `${reverse ? 'Worst Company of the Year' : 'Company of the Year'}: ${awardWinner.name} (${realYear - 1})`;
	await addCompanyLog(awardCompany, game, value, companyLogText, realYear);

	// Add reward
	if (reverse) {
		await addReward(data, awardCompany, game, -50)
	} else {
		await addReward(data, awardCompany, game, 200, true)
	}

	// Format data
	data['nominees'] = nominees.map(x => {
		return {
			name: x.name,
			rating: x.rating
		};
	});
	data['winner'] = {
		name: awardWinner['name'],
		rating: awardWinner['rating']
	};

	if (!reverse) {
		return [data, companiesData];
	} else {
		return data;
	}
}

async function getBestChampionship(championships, game, realYear) {  // get_best_championship
	let data = {};

	// Get award data
	data['name'] = 'Championship of the Year';
	data['trophy'] = 'silver';

	// Get best
	let sortedChampionships = [...championships].sort((a, b) => b.xpGainedThisYear - a.xpGainedThisYear);
	let bestChampionship = sortedChampionships[0];

	// Add nominees and shuffle
	let nominees = [];
	nominees.push(...sortedChampionships.slice(0, 3));
	shuffle(nominees);

	// Get award company
	let awardCompany = await getChampionshipCompany(bestChampionship);

	// Add company log
	const companyLogText = `Championship of the Year: ${getChampionshipDisplayName(bestChampionship)} (${realYear - 1})`;
	await addCompanyLog(awardCompany, game, 2, companyLogText, realYear);

	// Add reward
	await addReward(data, awardCompany, game, 100);

	// Format data
	data['nominees'] = nominees.map(x => {
		return {
			name: getChampionshipDisplayName(x),
			xpGained: x.xpGainedThisYear
		};
	});

	data['winner'] = {
		name: getChampionshipDisplayName(bestChampionship),
		averageRating: bestChampionship.xpGainedThisYear
	};

	return data;
}

async function getBestFaction(factions, game, realYear) {  // get_best_faction
	let data = {};

	// Get award data
	data['name'] = 'Faction of the Year';
	data['trophy'] = 'bronze';

	// Get best
	let sortedFactions = [...factions].sort((a, b) => b.xpGainedThisYear - a.xpGainedThisYear);
	let bestFaction = sortedFactions[0];

	// Add nominees and shuffle
	let nominees = [];
	nominees.push(...sortedFactions.slice(0, 3));
	shuffle(nominees);

	// Get award company
	let bestFactionRoster = await getFactionRoster(bestFaction);
	let awardCompany = await getRosterCompany(bestFactionRoster);

	// Add company log
	const companyLogText = `Faction of the Year: ${bestFaction.name} (${realYear - 1})`;
	await addCompanyLog(awardCompany, game, 1, companyLogText, realYear);

	// Add reward
	await addReward(data, awardCompany, game, 50);

	// Format data
	data['nominees'] = nominees.map(x => {
		return {
			name: x.name,
			xpGained: x.xpGainedThisYear
		};
	});
	data['winner'] = {
		name: bestFaction.name,
		averageRating: bestFaction.xpGainedThisYear
	};

	return data;
}

async function checkToAwardOrUnlockPowerups(data, company, game) {
	let powerups = await getCompanyPowerups(company);
	if (powerups.length) {
		let powerupsToUnlock = getPowerupsToUnlock(powerups);
        if (powerupsToUnlock.length > 0) {
            await awardOrUnlockPowerup(data, company, game, powerupsToUnlock, true);
        }
        else {
            await awardOrUnlockPowerup(data, company, game, powerups)
        }
	}
}

async function awardOrUnlockPowerup(data, company, game, allPowerups, unlock = false) {
    let unlockedOrAwardedPowerup;
    if (!unlock) {
        allPowerups = allPowerups.filter(y => y.unlocked);
    }
    unlockedOrAwardedPowerup = awardPowerup(allPowerups, unlock, true);
    if (company.userControlled) {
        let newMessage = await createPowerupMessage(unlockedOrAwardedPowerup, game.monthCount, game.year,
            unlock);
        await addCompanyMessages(company, [newMessage]);
    }
}

async function addReward(data, company, game, points, checkPowerups = false) {
	data['reward'] = {};
	data['companyName'] = company.name;
	if (company.userControlled) {
		data['reward']['points'] = points;
		company.points += points;
	}
	if (checkPowerups) {
		await checkToAwardOrUnlockPowerups(data, company, game);
	}
}

async function addCompanyLog(company, game, value, text, realYear) {
	const newLog = CompanyLog(game.monthCount, realYear);
	CompanyLogs.insert(newLog);
	newLog.text = text;
	newLog.type = 'award';
	newLog.realYear = game.year;
	newLog.value = value;
    await addCompanyCompanyLogs(company, [newLog]);
}

export {processAwards}